import React, { useCallback, useMemo } from 'react';
import { Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import _, { isEqual, partial, property, shuffle, times } from 'lodash';
import RubricBlock from '@dyl/blocks/RubricBlock';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import SectionDividers from "@dyl/components/SectionDividers";
import { Restore as ResetIcon, Shuffle as ShuffleIcon } from "@mui/icons-material";
import { ConnectingLinesRenderer } from "@dyl/components/ConnectingLines";
import Stimulus from "./Stimulus";
import Answer from "./Answer";


const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
    },
    groupHeader: {
        marginBottom: '16px',
        textAlign: 'center'
    },
    field: {
        // Default is inline-flex, but we want them on their own lines
        marginBottom: theme.spacing(2),
        // Normally we would use md * (3/4) which corresponds to 675px
        // But it looks ugly as it's just below the rubric element which is exactly 651px.
        maxWidth: theme.breakpoints.values.md, // '651px',
    },
}));


export default function Editor() {
    const classes = useStyles();
    const [items] = useTaskField('items');
    console.debug("Items", items);
    const phase1Items = items.filter(x => x.phase === 'DEFINITION');

    return (
        <div className={classes.root}>
            <SectionDividers>
                <>
                    <Typography variant='h4' className={classes.groupHeader}>Part 1</Typography>
                    {times(phase1Items.length, i => <Phase1Item key={i} index={i} />)}
                </>
                <>
                    <Typography variant='h4' className={classes.groupHeader}>Part 2</Typography>
                    <RubricBlock index={4} />
                    <Phase2Page />
                </>
            </SectionDividers>
        </div>
    );
}

function Phase1Item({ index }) {
    const classes = useStyles();
    const useField = partial(useTaskField, 'items', index);

    const [stimulus, setStimulus, stimulusError] = useField('answers', 0, 'fill_text');
    const onStimulusChange = useChangeEvent(setStimulus);

    const [key, setKey, keyError] = useField('answers', 0, 'key', 0);
    console.log('key', key);
    const onKeyChange = useChangeEvent(setKey);
    return (
        <>
            <Typography variant='h6'>
                {index === 0 ? 'Example' : `Item ${index}`}
            </Typography>

            {index === 0 && <RubricBlock />}

            <TextField
                required
                multiline
                minRows={2}
                // Scroll bar appears for extra rows
                maxRows={6}
                label='Stimulus Text'
                helperText={stimulusError || 'Enter the text above. End sentences with appropriate punctuation(.!?) and use a single space between sentences.  Use an underscore for each missing letter in the target word, e.g., “This c_ _ _d be fun”.'}
                error={!!stimulusError}
                value={stimulus ?? ''}
                onChange={onStimulusChange}
                variant='filled'
                fullWidth
                className={classes.field}
            />

            <TextField
                required
                label='Missing Letters (Key)'
                helperText={keyError || 'These letters complete the above stimulus text. In the above example the letters would be “oul”.'}
                error={!!keyError}
                value={key ?? ''}
                onChange={onKeyChange}
                variant='filled'
                fullWidth
                className={classes.field}
            />
        </>
    );
}


const ELEMENT_COUNT = 5;
function Phase2Page() {
    const [items, setItems] = useTaskField('items');
    const page = items.filter(x => x.phase === 'GAP_FILLING');

    const displayOrders = useMemo(() => _(page)
        .flatMap((item, index) => (
            item?.answers.map((a, i) => ({
                index: item.index,
                answerIndex: i,
                displayOrder: a.display_order,
                isDistractor: !a.is_correct_answer,
            }))))
        .sortBy(property('displayOrder'))
        .value(), [page]);
    console.debug('displayOrders', displayOrders);

    const resetOrder = useCallback(() => {
        let index = 1;
        const relevantItemIndices = page.map(x => x.index);
        const itemsNew = items.map((item) => {
            if (!relevantItemIndices.includes(item.index)) {
                item.answers.forEach(() => ++index);
                return item;
            }
            return {
                ...item,
                answers: item.answers.map(answer => ({
                    ...answer,
                    display_order: (index++ % ELEMENT_COUNT) + 1,
                })),
            };
        });
        if (!isEqual(items, itemsNew)) setItems(itemsNew);
    }, [items, page]);

    const base = 0;
    const shuffleOrder = useCallback(() => {
        const relevantItemIndices = page.map(x => x.index);
        const oldDisplayOrder = page.flatMap(x => x.answers.map(y => y.display_order));

        /* Attempt up to 100 times to get a different order to last time. */
        const displayOrder = (() => {
            let order = [];
            for (let attempt = 0; attempt < 100; attempt++) {
                order = shuffle(times(ELEMENT_COUNT, i => base + i + 1));
                if (!isEqual(order, oldDisplayOrder)) { break; }
            }
            return order;
        })();

        let index = 0;
        const itemsNew = items.map((item) => {
            if (!relevantItemIndices.includes(item.index)) return item;
            return {
                ...item,
                answers: item.answers.map(answer => ({
                    ...answer,
                    display_order: displayOrder[index++],
                })),
            };
        });
        setItems(itemsNew);
    }, [items]);

    return (
        <>
            <Grid container spacing={2} style={{ paddingBottom: "12px" }}>
                <Grid item xs={5} />
                <Grid item xs={1} />
                <Grid item xs={6} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <div  style={{ flex: 1 }}></div>
                    {/* Buttons */}
                    <Tooltip title='Reset Order'>
                        <IconButton onClick={resetOrder} tabIndex={-1} size="large">
                            <ResetIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Shuffle Order'>
                        <IconButton onClick={shuffleOrder} tabIndex={-1} size="large">
                            <ShuffleIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <ConnectingLinesRenderer>
                <Grid container spacing={2}>
                    <>
                        {/* Stimulus column */}
                        <Grid item xs={5}>
                            {page.map(({ index }) => {
                                return <Stimulus key={index} index={index} minRows={1} pageIndexOffset={4} />;
                            })}
                        </Grid>
                        <Grid item xs={1} />
                        <Grid item xs={6}>
                            {displayOrders.map(({ index, answerIndex, displayOrder, isDistractor }) => {
                                return <Answer
                                    key={20 * index + answerIndex}
                                    index={index}
                                    pageIndex={0}
                                    answerIndex={answerIndex}
                                    isDistractor={isDistractor}
                                    displayOrder={displayOrder}
                                    list={displayOrders}
                                    textFieldName={'fill_text'}
                                    pageIndexOffset={4}
                                />;
                            })}
                        </Grid>
                    </>
                </Grid>
            </ConnectingLinesRenderer>
        </>
    )
}